import { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import api from "@/lib/api";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import SafeImg from "@/components/common/SafeImg";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import SectionHeader from "./SectionHeader";
import { HOME_WRAP, SECTION_Y } from "./homeTheme";

/**
 * Client testimonials — shown ONLY when real. A testimonial appears on the
 * homepage only if it is Published, Featured AND explicitly marked "Verified real
 * customer testimonial" in Admin → Testimonials. The section renders nothing
 * otherwise, so demo/placeholder entries can never surface by accident.
 * No star ratings: the layout shows the quote, name, designation and company only.
 */
export default function Testimonials() {
  const [items, setItems] = useState([]);
  const autoplay = useRef(Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true }));

  useEffect(() => {
    api.get("/testimonials", { params: { status: "published", featured: true, limit: 100 } })
      .then((r) => setItems((r.data || []).filter((t) => t.verified === true && (t.quote || t.review || t.comment))))
      .catch(() => setItems([]));
  }, []);

  if (!items.length) return null;
  return (
    <section className={`bg-[#F7F9FC] ${SECTION_Y}`} aria-labelledby="home-testimonials-title">
      <div className={HOME_WRAP}>
        <SectionHeader eyebrow="Client Voices" title="What Our Clients Say" titleId="home-testimonials-title" className="mb-12 lg:mb-14" />
        <Carousel opts={{ align: "start", loop: items.length > 2 }} plugins={items.length > 1 ? [autoplay.current] : []} className="relative">
          <CarouselContent className="-ml-5">
            {items.map((t, i) => (
              <CarouselItem key={t.id || `${t.name}-${i}`} className="basis-full pl-5 md:basis-1/2 lg:basis-1/3">
                <figure className="flex h-full flex-col rounded-3xl border border-[#E4E9F2] bg-white p-8">
                  <Quote className="h-8 w-8 text-[#1769FF]/25" aria-hidden="true" />
                  <blockquote className="mt-5 flex-1 text-[17px] leading-relaxed text-[#13233F]">{t.quote || t.review || t.comment}</blockquote>
                  <figcaption className="mt-8 flex items-center gap-4 border-t border-[#EEF1F6] pt-6">
                    {(t.image || t.photo) && <SafeImg src={toAbsoluteUrl(t.image || t.photo)} alt="" loading="lazy" className="h-11 w-11 rounded-full object-cover" />}
                    <div>
                      <div className="font-heading text-[15px] font-semibold text-[#071B3A]">{t.name}</div>
                      <div className="mt-0.5 text-[13px] text-[#667085]">{[t.role || t.designation, t.company].filter(Boolean).join(" · ")}</div>
                    </div>
                  </figcaption>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
          {items.length > 3 && (<><CarouselPrevious className="-left-3 hidden lg:flex" /><CarouselNext className="-right-3 hidden lg:flex" /></>)}
        </Carousel>
      </div>
    </section>
  );
}
